import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SEOHead } from '../components/common/SEOHead';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { ProductCard } from '../components/common/ProductCard';
import { LeadFormDialog } from '../components/common/LeadFormDialog';
import { getBrand, getCategory, getProducts } from '../lib/api';
import { generateWhatsAppLink } from '../lib/utils';
import { MessageCircle } from 'lucide-react';

export default function BrandPage() {
  const { categorySlug, brandSlug } = useParams();
  const [brand, setBrand] = useState(null);
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQuoteForm, setShowQuoteForm] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const catRes = await getCategory(categorySlug);
        setCategory(catRes.data);

        // Brands are global; products still filtered by brand + category
        const brandRes = await getBrand(brandSlug);
        setBrand(brandRes.data);

        const prodRes = await getProducts({
          brand_id: brandRes.data.id,
          category_id: catRes.data.id,
        });
        setProducts(prodRes.data);
      } catch (error) {
        console.error('Error fetching brand:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [categorySlug, brandSlug]);

  if (loading) {
    return (
      <div className="pt-32 min-h-screen bg-[#F4F4F5]">
        <div className="container-custom">
          <div className="skeleton h-48 mb-8" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="skeleton h-72" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!brand || !category) {
    return (
      <div className="pt-32 min-h-screen">
        <div className="container-custom text-center py-20">
          <h1 className="heading-lg mb-4">Brand Not Found</h1>
          <Link to="/products" className="btn-primary">View All Products</Link>
        </div>
      </div>
    );
  }

  const whatsappMessage = `Hi, I'm interested in ${brand.name} ${category.name} products. Please share the catalog and wholesale pricing.`;
  const whatsappLink = generateWhatsAppLink(whatsappMessage);

  return (
    <>
      <SEOHead
        title={`${brand.name} ${category.name} - Wholesale Supplier`}
        description={brand.meta_description || `Shop authentic ${brand.name} ${category.name.toLowerCase()} at wholesale prices. Silver Perfumes - your trusted fragrance wholesaler.`}
        canonical={`https://silverperfumes.thevinculum.org.in/category/${category.slug}/brand/${brand.slug}`}
        ogImage={brand.logo}
      />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#0A0A0A]" data-testid="brand-hero">
        <div className="container-custom">
          <Breadcrumbs items={[
            { label: 'Products', href: '/products' },
            { label: category.name, href: `/category/${category.slug}` },
            { label: brand.name },
          ]} />
          <div className="mt-6 flex flex-col md:flex-row items-start md:items-center gap-8">
            {brand.logo && (
              <div className="w-32 h-32 bg-white p-4 flex items-center justify-center">
                <img src={brand.logo} alt={brand.name} className="max-w-full max-h-full" />
              </div>
            )}
            <div className="flex-1">
              <span className="label-text mb-2 block">Premium Wholesaler</span>
              <h1 className="heading-xl text-white mb-2">{brand.name}</h1>
              <p className="text-[#C0C4CC] text-lg mb-4">{category.name} Collection</p>
              {brand.description && (
                <p className="text-gray-300 max-w-xl mb-6">{brand.description}</p>
              )}
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => setShowQuoteForm(true)}
                  className="btn-silver"
                  data-testid="brand-quote-btn"
                >
                  Request {brand.name} Quote
                </button>
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary border-white text-white hover:bg-white hover:text-[#0A0A0A] flex items-center gap-2"
                  data-testid="brand-whatsapp-btn"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp Enquiry
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="py-16 bg-[#F4F4F5]" data-testid="brand-products">
        <div className="container-custom">
          <div className="flex justify-between items-center mb-8">
            <h2 className="heading-md">{brand.name} {category.name}</h2>
            <span className="text-gray-500">{products.length} products</span>
          </div>

          {products.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  category={category.name}
                  categorySlug={category.slug}
                  brand={brand.name}
                  brandSlug={brand.slug}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white">
              <p className="text-gray-500 mb-4">No {brand.name} {category.name.toLowerCase()} products listed yet.</p>
              <button onClick={() => setShowQuoteForm(true)} className="btn-primary">
                Request {brand.name} Catalog
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white" data-testid="brand-cta">
        <div className="container-custom text-center">
          <h2 className="heading-md mb-4">Looking for More {brand.name} Products?</h2>
          <p className="text-gray-600 mb-8 max-w-xl mx-auto">
            We have access to the complete {brand.name} catalog. Contact us for any 
            specific products you need.
          </p>
          <button 
            onClick={() => setShowQuoteForm(true)}
            className="btn-primary"
          >
            Request Complete Catalog
          </button>
        </div>
      </section>

      <LeadFormDialog
        isOpen={showQuoteForm}
        onClose={() => setShowQuoteForm(false)}
        sourcePageUrl={`/category/${category.slug}/brand/${brand.slug}`}
        interestCategoryId={category.id}
        interestBrandId={brand.id}
        showBuyerType={true}
        title={`Request ${brand.name} Quote`}
        subtitle={`Get wholesale pricing for ${brand.name} ${category.name.toLowerCase()}`}
      />
    </>
  );
}
